import { observable, action, computed, makeAutoObservable, makeObservable } from "mobx";
import { AuthApi, Configuration, UserDto, UserProfileApi } from "../../api-client";
import notificationStore from "../Notification/notificationStore";
import { registerAuthStore } from "../../api/axiosConfig";
import { DEVICE_ID_HEADER, getDeviceId } from "../../api/deviceId";

declare global {
  interface Window {
    __E2E_AUTH__?: {
      setToken: (token: string) => void;
      getToken: () => string | null;
      clearToken: () => void;
      /** Register a callback to be notified when the token changes */
      onTokenChange: (callback: (token: string | null) => void) => () => void;
    };
  }
}

export default class AuthStore {
  @observable authToken: string | null = null;

  @observable public isBootstrapping: boolean = true;

  public apiClient: AuthApi;

  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly legacyTokenStorageKeys: string[] = [
    "token",
    "authToken",
    "accessToken",
    "access_token",
    "jwt",
    "jwtToken",
    "longTermToken",
    "long_term_token",
  ];

  /** E2E-only: callbacks to notify when token changes */
  private e2eTokenChangeListeners: Set<(token: string | null) => void> = new Set();

  public constructor(apiClient: AuthApi) {
    makeObservable(this);
    this.apiClient = apiClient;

    this.getUserToken = this.getUserToken.bind(this);

    // Register this store so the axios interceptor and api clients can read
    // the in-memory token and trigger refreshes.
    registerAuthStore(this);

    this.clearLegacyTokenStorage();

    // Bootstrap is now triggered by AuthProvider with route awareness.
    // This prevents refresh calls on public auth pages like /login.

    // Optional test-only bridge for Playwright to set/get/clear in-memory token.
    this.setupE2EAuthBridge();
  }

  private setupE2EAuthBridge = () => {
    if (process.env.REACT_APP_E2E_AUTH_BRIDGE !== "true") return;

    window.__E2E_AUTH__ = {
      setToken: (token: string) => this.setAuthToken(token),
      getToken: () => this.authToken,
      clearToken: () => this.setAuthToken(null),
      onTokenChange: (callback: (token: string | null) => void) => {
        this.e2eTokenChangeListeners.add(callback);
        // Return unsubscribe function
        return () => this.e2eTokenChangeListeners.delete(callback);
      },
    };
  };

  /** Remove previously persisted auth tokens from old localStorage-based flow. */
  private clearLegacyTokenStorage = () => {
    if (typeof window === "undefined" || !window.localStorage) return;

    for (const key of this.legacyTokenStorageKeys) {
      if (window.localStorage.getItem(key) !== null) {
        window.localStorage.removeItem(key);
      }
    }
  };

  /**
   * On app startup, attempt a silent refresh using the HttpOnly refresh cookie.
   * Keeps the user logged in across reloads without storing the token in localStorage.
   * Called by AuthProvider with route awareness.
   */
  public bootstrapAuth = async () => {
    try {
      await this.refreshToken();
    } catch {
      // No valid refresh cookie -> user stays logged out
    } finally {
      this.setBootstrapping(false);
    }
  };

  @action
  public setBootstrapping = (value: boolean) => {
    this.isBootstrapping = value;
  };

  /**
   * Decodes a JWT and returns the payload (without verifying signature)
   */
  private decodeJwt(token: string): { exp?: number; iat?: number } | null {
    try {
      const base64Payload = token.split(".")[1];
      const payload = atob(base64Payload);
      return JSON.parse(payload);
    } catch {
      console.error("[AUTH] Failed to decode JWT");
      return null;
    }
  }

  /**
   * Schedules a proactive token refresh 60 seconds before expiry
   */
  public scheduleProactiveRefresh = () => {
    // Clear any existing scheduled refresh
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }

    if (!this.authToken) {
      return;
    }

    const payload = this.decodeJwt(this.authToken);
    if (!payload?.exp) {
      console.warn("[AUTH] Token has no expiry claim, cannot schedule proactive refresh");
      return;
    }

    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;
    const refreshBuffer = 60 * 1000; // 60 seconds before expiry

    // Calculate when to refresh (60 seconds before expiry)
    const refreshIn = timeUntilExpiry - refreshBuffer;

    if (refreshIn <= 0) {
      // Token already expired or about to expire, refresh immediately
      console.info("[AUTH] Token expired or expiring soon, refreshing immediately");
      this.performProactiveRefresh();
      return;
    }

    console.info(
      `[AUTH] Scheduled proactive token refresh in ${Math.round(refreshIn / 1000)}s ` +
        `(token expires in ${Math.round(timeUntilExpiry / 1000)}s)`,
    );

    this.refreshTimeoutId = setTimeout(() => {
      this.performProactiveRefresh();
    }, refreshIn);
  };

  /**
   * Performs the actual proactive refresh
   */
  private performProactiveRefresh = async () => {
    console.info("[AUTH] Performing proactive token refresh...");
    const success = await this.refreshToken();
    if (!success) {
      console.error("[AUTH] Proactive token refresh failed");
      // Token refresh failed - user will be logged out on next 401
    }
  };

  setAuthToken = (token: string | null) => {
    this.authToken = token;

    // Notify E2E listeners (if any registered via bridge)
    this.e2eTokenChangeListeners.forEach((cb) => cb(token));

    if (token) {
      // Memory-only: schedule proactive refresh for the new token.
      // WebSocket and api clients observe authToken directly.
      this.scheduleProactiveRefresh();
    } else if (this.refreshTimeoutId) {
      // Clear any scheduled refresh
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  };

  //   @computed
  //   public get isAuthenticated() {
  //     console.log('thiiuuk',this.authToken)
  //     return this.authToken !== '';
  //   }

  public logout = async () => {
    try {
      await this.apiClient.authControllerLogout();
    } catch (error: any) {
      console.error("Logout API error:", error?.response?.status, error?.message);
    } finally {
      this.setAuthToken(null);
    }
  };

  public refreshToken = async () => {
    try {
      const response = await this.apiClient.authControllerRefresh();

      if (response.data?.access_token) {
        this.setAuthToken(response.data.access_token);
        return true;
      }
      return false;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Expected when no refresh cookie/session exists.
        console.info("No refresh session available");
      } else {
        console.error("Token refresh error:", error?.response?.status, error?.message);
      }
      return false;
    }
  };

  public getUserToken = async (userDto: UserDto) => {
    try {
      const response: any = await this.apiClient.authControllerLogin(userDto, {
        headers: { [DEVICE_ID_HEADER]: getDeviceId() },
      });
      this.setAuthToken(response.data.access_token);
      // console.log('Auth Token after login',this.authToken)
      // notificationStore.showMessage('Login Successful','success');
      return this.authToken;
    } catch (error: any) {
      // Avoid logging the full axios error: error.config.data may contain credentials.
      console.error("Error fetching authToken:", error?.response?.status, error?.message);
      const errorMessage = error.response?.data?.message || "Fehler bei der Anmeldung";

      notificationStore.showMessage(errorMessage, "error", error);
    }
  };
}
